'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Locale, defaultLocale } from '@/i18n/config';
import type { Product } from '@/payload-types';

type ProductsResponse = {
    docs: Product[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    hasNextPage: boolean;
    nextPage: number | null;
};

const pageSize = 6;

const getStatusLabel = (status: Product['status'], t: (key: string) => string) => {
    switch (status) {
        case 'valid':
            return t('VrifyPage.deistvitelen');
        case 'revoked':
            return t('VrifyPage.otozvan');
        case 'expired':
            return t('VrifyPage.istyok');
        default:
            return t('VrifyPage.neizvestno');
    }
};

const getStatusVariant = (status: Product['status']) => {
    switch (status) {
        case 'valid':
            return 'default';
        case 'revoked':
            return 'destructive';
        case 'expired':
            return 'outline';
        default:
            return 'secondary';
    }
};

export const ProductsForCompany = ({ idCompany, locale }: { idCompany: number; locale: Locale }) => {
    const t = useTranslations();
    const [items, setItems] = useState<Product[]>([]);
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const queryParamsBase = useMemo(() => {
        const params = new URLSearchParams();
        params.set('limit', String(pageSize));
        params.set('depth', '3');
        params.set('draft', 'false');
        params.set('locale', locale);
        params.set('fallbackLocale', defaultLocale);
        params.set('where[company][equals]', String(idCompany));
        // params.append('where[status][not_in]', 'revoked');
        // params.append('where[status][not_in]', 'expired');
        params.set('sort', '-createdAt');
        return params;
    }, [idCompany, locale]);

    const loadProducts = useCallback(
        async (nextPage: number, replace: boolean) => {
            setIsLoading(true);
            setError(null);
            const controller = new AbortController();
            try {
                const params = new URLSearchParams(queryParamsBase);
                params.set('page', String(nextPage));
                const response = await fetch(`/api/products?${params.toString()}`, {
                    method: 'GET',
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`Ошибка загрузки: ${response.status}`);
                }

                const data = (await response.json()) as ProductsResponse;
                setItems((prev) => (replace ? data.docs : [...prev, ...data.docs]));
                setPage(data.page);
                setHasNextPage(data.hasNextPage);
            } catch (fetchError) {
                const message = fetchError instanceof Error ? fetchError.message : 'Не удалось загрузить товары';
                setError(message);
            } finally {
                setIsLoading(false);
            }
        },
        [queryParamsBase]
    );

    useEffect(() => {
        setItems([]);
        setPage(1);
        setHasNextPage(false);
        loadProducts(1, true);
    }, [loadProducts]);

    if (!items.length) return null;

    return (
        <section className="space-y-6 mb-42 container mx-auto">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold">{t('CompaniesPage.tovaryOrganiza')}</h2>
            </div>
            {error ? <div className="text-destructive text-sm">{error}</div> : null}
            {items.length ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((product) => (
                        <Card key={product.id} className="h-full flex flex-col justify-between">
                            <CardHeader>
                                <div className="flex items-start justify-between gap-4">
                                    <CardTitle className="text-lg leading-snug">
                                        {product.name || 'Без названия'}
                                    </CardTitle>
                                    <Badge variant={getStatusVariant(product.status ?? null)}>
                                        {getStatusLabel(product.status ?? null, t)}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-2 text-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-muted-foreground">{t('VrifyPage.artikulSku')}</span>
                                    <span className="font-mono">{product.sku || '—'}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-muted-foreground">{t('VrifyPage.kodProverki')}</span>
                                    <span className="font-mono">{product.verify_code || '—'}</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="text-muted-foreground text-sm">{t('CompaniesPage.tovaryNeNaide')}</div>
            )}
            {hasNextPage ? (
                <div className="flex justify-center">
                    <Button onClick={() => loadProducts(page + 1, false)} disabled={isLoading}>
                        {isLoading ? `${t('Base.zagruzka')}...` : `${t('Base.zagruzitEshyo')}`}
                    </Button>
                </div>
            ) : null}
        </section>
    );
};
